<template>
  <div class="bg-white rounded-md p-5">
    <div
      v-if="loading"
      class="w-full flex items-center justify-center py-12"
    >
      Loading chart...
    </div>
    <div v-else>
      <div
        v-if="hasData"
        class="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <div class="chart-card">
          <p class="font-semibold mb-3">
            Sailing Performance Tug Owner {{ monthYear }} 
          </p>
          <div class="chart-wrapper">
            <BarChart
              v-if="sailingChartData"
              :data="sailingChartData"
              :options="barOptions"
              :plugins="barPlugins"
              :height="chartHeight"
            />
            <div
              v-else
              class="text-sm text-gray-500 text-center py-8"
            >
              No sailing performance data.
            </div>
          </div>
        </div>
        <div class="chart-card">
          <p class="font-semibold mb-3">
            General Performance Tug Owner {{ monthYear }}
          </p>
          <div class="chart-wrapper">
            <BarChart
              v-if="generalChartData"
              :data="generalChartData"
              :options="barOptions"
              :plugins="barPlugins"
              :height="chartHeight"
            />
            <div
              v-else
              class="text-sm text-gray-500 text-center py-8"
            >
              No general performance data.
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="text-center text-gray-500 py-10"
      >
        No chart data available.
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '@/components/barChart'

export default {
  name: 'PerformanceChart',
  components: { BarChart },
  props: {
    chartData: {
      type: [Array, Object],
      default: () => []
    },
    monthYear: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartHeight: 320,
      palette: [
        '#255594',
        '#258094',
        '#259487',
        '#259457',
        '#669425',
        '#948425',
        '#947725',
        '#945925',
        '#944625'
      ]
    }
  },
  computed: {
    sailingSeries() {
      return this.normalizeSeries(this.extractSeries('sailing'))
    },
    generalSeries() {
      return this.normalizeSeries(this.extractSeries('general'))
    },
    sailingChartData() {
      return this.buildChartData(this.sailingSeries, 'sailing')
    },
    generalChartData() {
      return this.buildChartData(this.generalSeries, 'general')
    },
    hasData() {
      return (this.sailingSeries.labels.length + this.generalSeries.labels.length) > 0
    },
    barOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        layout: {
          padding: {
            top: 10,
            right: 40,
            bottom: 10,
            left: 0
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { display: false },
            ticks: {
              callback: value => Number(value).toFixed(2)
            }
          },
          y: {
            grid: { display: false },
            ticks: {
              font: { family: 'Poppins' }
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title(context) {
                return context?.[0]?.label || ''
              },
              label(context) {
                const val = context?.raw
                const lines = []
                if (val !== null && val !== undefined) {
                  lines.push(`Score: ${Number(val).toFixed(2)}`)
                }

                const kind = context?.dataset?.kind
                if (kind === 'sailing') {
                  const meta = context?.dataset?.meta?.[context.dataIndex] || {}
                  const timeVal = meta?.time
                  if (timeVal !== null && timeVal !== undefined && Number.isFinite(Number(timeVal))) {
                    lines.push(`Time: ${Number(timeVal).toFixed(0)} hrs`)
                  }
                }

                return lines.length ? lines : ''
              }
            }
          }
        }
      }
    },
    barPlugins() {
      return [this.valueLabelPlugin()]
    }
  },
  methods: {
    extractSeries(kind) {
      const data = this.chartData || {}
      const keyCandidates = [
        `${kind}_performance`,
        `${kind}_performances`,
        `${kind}Performance`,
        `${kind}Performances`,
        `${kind}`,
        `${kind}Chart`
      ]

      const byKey = keyCandidates
        .map(key => data?.[key])
        .find(val => Array.isArray(val))

      if (byKey) return byKey

      if (Array.isArray(data)) {
        return data.filter(item => {
          const type = (item?.type || item?.category || item?.chart_type || '').toString().toLowerCase()
          return type.includes(kind.slice(0, 4))
        })
      }

      return []
    },
    normalizeSeries(series) {
      const mapped = (series || []).map(item => {
        const label = item?.owner_name || item?.owner || item?.ownerName || item?.tug_owner || item?.name || item?.label || item?.title
        const value = item?.overall_score ?? item?.score ?? item?.value ?? item?.total_score ?? item?.performance ?? null
        const time = item?.time ?? item?.sailing_time ?? item?.sailingTime ?? null
        return {
          label,
          value: value !== null && value !== undefined ? Number(value) : null,
          time: time !== null && time !== undefined && Number.isFinite(Number(time)) ? Number(time) : null
        }
      }).filter(item => item.label && Number.isFinite(item.value))

      mapped.sort((a, b) => b.value - a.value)

      return {
        labels: mapped.map(m => m.label),
        values: mapped.map(m => Number(m.value.toFixed(2))),
        meta: mapped.map(m => ({ time: m.time }))
      }
    },
    buildChartData(series, kind) {
      if (!series.labels.length) return null
      const colors = this.palette

      return {
        labels: series.labels,
        datasets: [
          {
            data: series.values,
            backgroundColor: series.labels.map((_, idx) => colors[idx % colors.length]),
            borderWidth: 0,
            barThickness: 28,
            borderRadius: 6,
            kind,
            meta: series.meta || []
          }
        ]
      }
    },
    valueLabelPlugin() {
      return {
        id: 'valueLabel',
        afterDatasetsDraw: chart => {
          const { ctx } = chart
          ctx.save()
          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex)
            meta.data.forEach((element, index) => {
              const value = dataset.data[index]
              if (value === null || value === undefined) return

              const pos = element.tooltipPosition()
              ctx.fillStyle = '#1f2937'
              ctx.font = 'bold 12px Poppins'
              ctx.textAlign = 'left'
              ctx.textBaseline = 'middle'
              ctx.fillText(Number(value).toFixed(2), pos.x + 8, pos.y)
            })
          })
          ctx.restore()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-card {
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
}
.chart-wrapper {
  min-height: 320px;
}
</style>
